import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import GoogleLogoSvg from 'shells/logos/google-g.svg';

import type {
    setGoogleMailSendIsEnabled as setGoogleMailSendIsEnabledMutation,
    setGoogleMailSendIsEnabledVariables as setGoogleMailSendIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {authToService} from '../../integrations/auth-to-service';
import {ClearIntegration} from '../../integrations/clear-integration';
import {getAuthUrl} from '../../integrations/integration-mutation-response-helpers';

import {QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE} from '../constants';
import SET_GOOGLE_MAIL_SEND_IS_ENABLED from '../graphql/mutations/set-google-mail-send-is-enabled.graphql';

import {updateCacheFromMutation} from './update-cache-from-mutation';

const googleLogo = <GoogleLogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

class GoogleEnableMutation extends Mutation<
    setGoogleMailSendIsEnabledMutation,
    setGoogleMailSendIsEnabledMutationVariables
> {}

type Props = {
    username: string;
    isEnabled: boolean;
    onAuthStart: () => void;
    onAuthSuccess: () => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    externalService: 'GOOGLE' | 'GOOGLE_APPS';
    errors?: string[];
};

export function EmailWidgetGoogle(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);

    return (
        <ClearIntegration>
            {({clearIntegration}) => (
                <GoogleEnableMutation
                    mutation={SET_GOOGLE_MAIL_SEND_IS_ENABLED}
                    update={updateCacheFromMutation}
                    // @ts-expect-error
                    refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
                >
                    {(enableGoogle, {loading: isMutationInFlight}) => {
                        const hasErrors = props.errors && props.errors.length > 0;
                        let status = props.isEnabled && !hasErrors ? 'enabled' : 'disabled';
                        if (isMutationInFlight || isAuthenticating) {
                            status = props.isEnabled ? 'disabling' : 'enabling';
                        }

                        return (
                            <IntegrationWidget
                                // @ts-expect-error
                                status={status}
                                account={props.username}
                                errors={props.errors}
                                logo={googleLogo}
                                onConnectClick={() => {
                                    enableGoogle({variables: {isEnabled: true}})
                                        .then(getAuthUrl)
                                        .then((authUrl) => {
                                            // If we get an authUrl back, then we need to auth
                                            // Highly unlikely in the case of google,
                                            // because we get all scopes up front.
                                            if (authUrl) {
                                                props.onAuthStart();
                                                setIsAuthenticating(true);
                                                authToService({
                                                    authUrl,
                                                    windowFeatures,
                                                    authCheckUrl: '/google/has-auth',
                                                })
                                                    .then(({hasAuth}) => {
                                                        if (hasAuth) {
                                                            props.onAuthSuccess().then(() => {
                                                                setIsAuthenticating(false);
                                                            });
                                                        }
                                                    })
                                                    .catch((errMsg) => {
                                                        props.onAuthError(errMsg);
                                                        setIsAuthenticating(false);
                                                    });
                                            }
                                        });
                                }}
                                onDisconnectClick={() => {
                                    enableGoogle({variables: {isEnabled: false}});
                                }}
                                revokeWarning='This will also remove this account from calendar and task sync.'
                                onRevokeClick={() => {
                                    if (props.externalService !== 'GOOGLE_APPS') {
                                        props.onAuthStart();
                                        clearIntegration('GOOGLE')
                                            .then(() => {
                                                props.onAuthSuccess();
                                            })
                                            // TODO: does this actually work?
                                            .catch((errMsg) => {
                                                props.onAuthError(errMsg);
                                            });
                                    }
                                }}
                            />
                        );
                    }}
                </GoogleEnableMutation>
            )}
        </ClearIntegration>
    );
}
